import { useWorkflowMutation, useWorkflowCompletion } from "./react.tsx";
import { workflowControlBusyKey } from "./mutation-model.ts";
import { parseWorkflowJson } from "./form.ts";
import { Button, Dialog } from "@cloudflare/kumo";
import { useCallback, useEffect, useState } from "react";

// Adapted from workers-sdk's local-explorer-ui Send Event dialog (inlined
// twice upstream; extracted here), wired to this project's controlWorkflow.
interface WorkflowSendEventDialogProps {
  workspaceId: string;
  instanceId: string;
  open: boolean;
  disabled?: boolean;
  onOpenChange: (open: boolean) => void;
  onMutationStart?: () => boolean;
  onMutationEnd?: () => void;
  onSent?: () => void;
}

export function WorkflowSendEventDialog({
  workspaceId,
  instanceId,
  open,
  disabled = false,
  onOpenChange,
  onMutationStart,
  onMutationEnd,
  onSent
}: WorkflowSendEventDialogProps) {
  const selectionKey = JSON.stringify(["event", workspaceId, instanceId]);
  const mutation = useWorkflowMutation(selectionKey, open, workflowControlBusyKey(workspaceId, instanceId));
  const [eventType, setEventType] = useState("");const [eventPayload, setEventPayload] = useState("");const [payloadError, setPayloadError] = useState<string | null>(null);
  const sending = mutation.busy, error = mutation.snapshot.error;
  const resetForm = useCallback(() => { setEventType("");setEventPayload("");setPayloadError(null); }, []);
  useEffect(() => { resetForm(); }, [selectionKey, open, resetForm]);
  function handleOpenChange(next: boolean) { if (!next) { mutation.dispatch({ _tag: "Select", selectionKey, active: false });resetForm(); }onOpenChange(next); }
  useWorkflowCompletion(mutation, snapshot => { onMutationEnd?.();if (snapshot.result) { handleOpenChange(false);onSent?.(); } });
  function handleSend(): void {
    if (disabled || sending || !eventType.trim()) return;
    const parsed = parseWorkflowJson(eventPayload);if (!parsed.valid) { setPayloadError("Invalid JSON");return; }setPayloadError(null);
    if (onMutationStart && !onMutationStart()) return;
    mutation.dispatch({ _tag: "Control", selectionKey, target: { workspaceId, instanceId }, control: { action: "sendEvent", type: eventType.trim(), ...(parsed.value === undefined ? {} : { payload: parsed.value }) } });
  }

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog size="lg" className="w-lg">
        <div className="border-b border-kumo-fill px-6 py-4">
          <Dialog.Title className="text-lg font-semibold text-kumo-default">
            Send Event
          </Dialog.Title>
        </div>
        <div className="space-y-4 px-6 py-5">
          {error && (
            <div className="rounded-lg border border-kumo-danger/20 bg-kumo-danger/8 p-3 text-sm text-kumo-danger">
              {error}
            </div>
          )}
          <div>
            <label
              className="mb-2 block text-sm font-medium text-kumo-default"
              htmlFor="workflow-event-type"
            >
              Event Type
            </label>
            <input
              id="workflow-event-type"
              className="focus-visible:ring-kumo-ring w-full rounded-lg border border-kumo-fill bg-kumo-base px-3 py-2.5 text-sm text-kumo-default placeholder:text-kumo-subtle focus:border-kumo-brand focus:outline-none focus-visible:ring-2"
              onChange={(e) => setEventType(e.target.value)}
              placeholder="my-event"
              type="text"
              value={eventType}
              disabled={disabled || sending}
            />
          </div>
          <div>
            <label
              className="mb-2 block text-sm font-medium text-kumo-default"
              htmlFor="workflow-event-payload"
            >
              Payload{" "}
              <span className="font-normal text-kumo-subtle">(optional)</span>
            </label>
            <textarea
              id="workflow-event-payload"
              className={`focus-visible:ring-kumo-ring w-full resize-y rounded-lg border bg-kumo-base px-3 py-2.5 font-mono text-sm text-kumo-default placeholder:text-kumo-subtle focus:outline-none focus-visible:ring-2 ${
                payloadError
                  ? "border-kumo-danger focus:border-kumo-danger"
                  : "border-kumo-fill focus:border-kumo-brand"
              }`}
              onChange={(e) => {
                setEventPayload(e.target.value);
                if (payloadError) {
                  setPayloadError(null);
                }
              }}
              placeholder='{"key": "value"}'
              rows={4}
              value={eventPayload}
              disabled={disabled || sending}
            />
            {payloadError && (
              <p className="mt-1 text-xs text-kumo-danger">{payloadError}</p>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-kumo-fill px-6 py-4">
          <Button
            variant="secondary"
            onClick={() => handleOpenChange(false)}
            disabled={sending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={disabled || !eventType.trim() || sending || !mutation.ready}
            aria-busy={!mutation.ready}
            loading={sending}
            onClick={() => void handleSend()}
          >
            Send
          </Button>
        </div>
      </Dialog>
    </Dialog.Root>
  );
}
